import Experience from "../Experience";
import * as THREE from "three";
import { gsap } from "gsap";
import { decode } from "blurhash";
import DataFetcher from "../Data/arenafetcher";
import { billboardEls, interactedObjects } from "./sets";
import type { ArenaBlock, ArenaImage } from "../types/arena";
import { debugLog } from "../Utils/debugLog";

type RegenMeshData = {
  name?: string;
  position?: THREE.Vector3;
  blockID?: number | string;
  blockType?: string;
  content?: unknown;
  creationDate?: string;
  description?: string;
  direction?: THREE.Vector3;
  generatedTitle?: string;
  htmlContent?: string;
  image?: ArenaImage | null;
  positionUserData?: unknown;
  source?: unknown;
  title?: string;
  user?: unknown;
  connections?: unknown[];
  class?: string;
  base_class?: string;
  state?: string;
  attachment?: { image?: { src?: string }; preview_url?: string };
  screenshotURL?: string;
  embed?: { image_url?: string };
};
type PlaneData = ArenaBlock | RegenMeshData | null;
type TextureLoaderLike = {
  load: (
    url: string,
    onLoad: (texture: THREE.Texture) => void,
    onProgress?: ((event: ProgressEvent<EventTarget>) => void) | undefined,
    onError?: ((event: unknown) => void) | undefined
  ) => void;
};
type ConnectionManagerLike = {
  setBlockData: (block: THREE.Object3D, data: ArenaBlock) => void;
  fetchConnections: (data: ArenaBlock, inputEl: THREE.Object3D) => Promise<unknown>;
};

type PlaneCtorArgs = {
  endpoint?: string | null;
  data?: PlaneData;
};

export default class Plane {
  endpoint: string | null;
  data: PlaneData;
  experience: Experience;
  scene: THREE.Scene;
  camera: THREE.Camera;
  resources: Experience["resources"];
  textureLoader: TextureLoaderLike;
  dataFetcher: DataFetcher;
  connectionManager: ConnectionManagerLike;
  geometry!: THREE.PlaneGeometry;
  material!: THREE.MeshBasicMaterial;
  mesh!: THREE.Mesh;

  constructor({ endpoint = null, data = null }: PlaneCtorArgs = {}) {
    this.endpoint = endpoint;
    this.data = data;
    this.experience = new Experience();
    this.scene = this.experience.scene as THREE.Scene;
    this.camera = this.experience.camera?.instance as THREE.Camera;
    this.resources = this.experience.resources;
    this.textureLoader = this.experience.resources.loaders!.textureLoader as TextureLoaderLike;
    this.dataFetcher = new DataFetcher(this.endpoint);
    this.connectionManager = this.experience.connectionManager as ConnectionManagerLike;

    if (this.data && this.data.name === "RegenMesh") {
      this.regenMesh();
    }
  }

  regenMesh() {
    this.setTexture(this.data).then((texture) => {
      this.setGeometry(texture);
      this.setMaterial(texture);
      this.mesh = this.setMesh();
      const regenData = this.data as RegenMeshData;
      const position = regenData.position ?? new THREE.Vector3();
      this.mesh.position.set(position.x, position.y, position.z);
      this.mesh.userData.blockID = regenData.blockID;
      this.mesh.userData.id = regenData.blockID;
      this.mesh.userData.blockType = regenData.blockType;
      this.mesh.userData.content = regenData.content;
      this.mesh.userData.creationDate = regenData.creationDate;
      this.mesh.userData.description = regenData.description;
      this.mesh.userData.direction = regenData.direction;
      this.mesh.userData.generatedTitle = regenData.generatedTitle;
      this.mesh.userData.htmlContent = regenData.htmlContent;
      this.mesh.userData.image = regenData.image;
      this.mesh.userData.name = regenData.name;
      this.mesh.userData.position = regenData.position;
      this.mesh.userData.positionUserData = regenData.positionUserData;
      this.mesh.userData.source = regenData.source;
      this.mesh.userData.title = regenData.title;
      this.mesh.userData.user = regenData.user;
      this.mesh.userData.connections = regenData.connections;
      this.mesh.userData.class = regenData.class;
      this.mesh.userData.base_class = regenData.base_class;
      this.mesh.userData.state = regenData.state;
      this.addToScene();
      billboardEls.add(this.mesh);
      interactedObjects.push(this.mesh);
    });
  }

  fetchAndCreate() {
    return this.dataFetcher
      .fetchData()
      .then((data) => {
        this.data = data;
        return this.create();
      })
      .then((mesh) => {
        return mesh;
      })
      .catch((error) => {
        console.error("Error in fetchAndCreate process:", error);
        throw error;
      });
  }

  create() {
    return this.setTexture(this.data)
      .then((texture) => {
        this.setGeometry(texture);
        this.setMaterial(texture);
        this.setMesh();
        if (this.isArenaBlock(this.data)) {
          this.connectionManager.setBlockData(this.mesh, this.data);
        }
        return this.mesh;
      })
      .catch((error) => {
        console.error("Error in create process:", error);
        throw error;
      });
  }

  setTexture(data: PlaneData) {
    const textureUrl = this.resolveTextureUrl(data);
    if (!textureUrl) {
      debugLog("No display img url", data);
      return Promise.reject(new Error(`No texture URL for block ${this.isArenaBlock(data) ? data.id : "unknown"}`));
    }
    const blurhash = this.resolveBlurhash(data);
    if (!blurhash) {
      return this.loadTextureFromUrl(textureUrl);
    }
    const placeholderTexture = this.createBlurhashTexture(blurhash, 32, 32);
    this.loadTextureFromUrl(textureUrl)
      .then((fullTexture) => {
        this.applyLoadedTexture(fullTexture);
      })
      .catch((error) => {
        debugLog("Failed full-res texture after blurhash placeholder", error);
      });
    return Promise.resolve(placeholderTexture);
  }
  loadTextureFromUrl(textureUrl: string) {
    return new Promise<THREE.Texture>((resolve, reject) => {
      this.textureLoader.load(
        textureUrl,
        (texture: THREE.Texture) => {
          texture.minFilter = THREE.LinearFilter;
          texture.colorSpace = THREE.SRGBColorSpace;
          resolve(texture);
        },
        undefined,
        reject
      );
    });
  }
  resolveBlurhash(data: PlaneData) {
    const image = data?.image as ArenaImage | null | undefined;
    return image?.blurhash || null;
  }
  createBlurhashTexture(blurhash: string, width: number, height: number) {
    const pixels = decode(blurhash, width, height);
    const texture = new THREE.DataTexture(pixels, width, height, THREE.RGBAFormat);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.needsUpdate = true;
    return texture;
  }
  applyLoadedTexture(texture: THREE.Texture) {
    if (!this.material) return;
    const previousTexture = this.material.map;
    this.material.map = texture;
    this.material.needsUpdate = true;
    if (previousTexture && previousTexture !== texture) {
      previousTexture.dispose();
    }
  }

  resolveTextureUrl(data: PlaneData) {
    const image = data?.image as ArenaImage | null | undefined;
    const attachment = data?.attachment as
      | { image?: { src?: string }; preview_url?: string }
      | undefined;
    return (
      image?.medium?.src ||
      image?.small?.src ||
      image?.large?.src ||
      image?.display?.url ||
      image?.square?.src ||
      image?.src ||
      attachment?.image?.src ||
      attachment?.preview_url ||
      data?.screenshotURL ||
      data?.embed?.image_url ||
      null
    );
  }

  setGeometry(texture: THREE.Texture) {
    const image = this.data?.image as ArenaImage | null | undefined;
    const textureImage = texture.image as { width?: number; height?: number };
    const width = image?.width || textureImage?.width || 1;
    const height = image?.height || textureImage?.height || 1;
    const aspectRatio = width / height;
    let planeWidth = 1 * aspectRatio;
    let planeHeight = 1;
    if (aspectRatio > 2) {
      planeWidth = 2;
      planeHeight = planeWidth / aspectRatio;
    }
    this.geometry = new THREE.PlaneGeometry(planeWidth, planeHeight);
  }

  setMaterial(texture: THREE.Texture) {
    this.material = new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.DoubleSide,
    });
  }

  setMesh() {
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.userData.instance = this.getLimitedInterface();
    this.mesh.userData.position = this.mesh.position.clone();
    this.mesh.scale.set(0, 0, 0);
    gsap.to(this.mesh.scale, {
      duration: 0.3,
      x: 1,
      y: 1,
      z: 1,
      ease: "power2.out",
    });
    return this.mesh;
  }

  getLimitedInterface() {
    return {
      updatePosition: this.updatePosition.bind(this),
      onHover: this.onHover.bind(this),
      onClick: this.onClick.bind(this),
      addToScene: this.addToScene.bind(this),
    };
  }

  onClick() {
    debugLog("clicked", this.data, this.isArenaBlock(this.data) ? this.data.id : undefined, this.data?.connections);
    if (this.isArenaBlock(this.data) && this.data.id) {
      if (this.mesh?.parent?.name === "pathwayItem") {
        return;
      }
      return this.connectionManager.fetchConnections(this.data, this.mesh);
    }
  }

  onHover() {
    this.experience.sceneUtils?.billboardPlane(this.mesh);
  }

  async addToScene() {
    this.scene.add(this.mesh);
    await this.experience.sceneUtils?.createDataLabels(this.mesh);
    return this.mesh;
  }

  updatePosition(newPosition: THREE.Vector3) {
    if (this.mesh) {
      this.mesh.position.copy(newPosition);
    }
  }

  private isArenaBlock(data: PlaneData): data is ArenaBlock {
    return Boolean(data && typeof data === "object" && "id" in data && "type" in data);
  }
}
